import { observer } from "mobx-react-lite";
import styled from "styled-components";

import { useStores } from "../../stores/useStores";
import { color } from "../../theme/color";
import { Button, IconButton, SmallHeading } from "../ui/controls";
import { Icon } from "../ui/Icon";
import { Segmented } from "../ui/Segmented";

/** What the sheet shows: everything, or the lyrics only for singers. */
const SHOW: ["chords" | "lyrics", string][] = [
    ["chords", "Chords and lyrics"],
    ["lyrics", "Lyrics only"],
];

/** Whether a section that doesn't fit at the bottom of a column moves to the next one, or is split to fill it. */
const SECTIONS: ["keep" | "split", string][] = [
    ["keep", "Whole sections"],
    ["split", "Split sections"],
];

/** Viewing settings, which never change the chord sheet, and the way to change the chord sheet itself. */
export const DisplayPanel = observer(function DisplayPanel({ onEdit }: { onEdit(): void }) {
    const { viewer, display, navigation } = useStores();
    const transpose = viewer.transpose;
    const show = display.lyricsOnly ? "lyrics" : "chords";
    const kept = viewer.from.kind === "setlist" ? "kept for this setlist" : "only while open";
    const transposed = transpose === 0 ? "as written" : `${transpose > 0 ? "+" : ""}${transpose}, ${kept}`;

    return (
        <Panel role="dialog" aria-label="Display settings">
            <SmallHeading as="div">View only · doesn't change the chord sheet</SmallHeading>
            <Setting>
                <SettingName>
                    Key <Muted>({transposed})</Muted>
                </SettingName>
                <IconButton $bordered aria-label="Transpose down" onClick={() => viewer.changeTranspose(-1)}>
                    <Icon name="minus" />
                </IconButton>
                <Value>{viewer.sheet.key ?? "–"}</Value>
                <IconButton $bordered aria-label="Transpose up" onClick={() => viewer.changeTranspose(1)}>
                    <Icon name="plus" />
                </IconButton>
            </Setting>
            <Setting>
                <SettingName>Text size</SettingName>
                <IconButton $bordered aria-label="Smaller text" onClick={() => display.changeTextSize(-2)}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>A</span>
                </IconButton>
                <IconButton $bordered aria-label="Larger text" onClick={() => display.changeTextSize(2)}>
                    <span style={{ fontSize: 21, fontWeight: 600 }}>A</span>
                </IconButton>
            </Setting>
            <Segmented label="Show" options={SHOW} value={show} onChange={(value) => display.setLyricsOnly(value === "lyrics")} />
            <Segmented
                label="Sections"
                options={SECTIONS}
                value={display.splitSections ? "split" : "keep"}
                onChange={(value) => display.setSplitSections(value === "split")}
            />
            <More $kind="quiet" onClick={() => navigation.go({ name: "settings", page: "appearance" })}>
                Colours, spacing and font…
            </More>
            <Divider>
                <span style={{ flex: 1 }}>Changing the chord sheet itself is saved.</span>
                <Button onClick={onEdit}>Edit</Button>
            </Divider>
        </Panel>
    );
});

const Panel = styled.div`
    position: absolute;
    top: 56px;
    right: 12px;
    z-index: 5;
    width: min(340px, calc(100vw - 24px));
    padding: 16px;
    border-radius: 16px;
    background: ${color.background};
    border: 1px solid ${color.border};
    box-shadow: 0 12px 32px ${color.shadow};
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const Setting = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SettingName = styled.span`
    flex: 1;
    font-size: 14px;
    color: ${color.textSoft};
`;

const Muted = styled.span`
    color: ${color.textMuted};
`;

const Value = styled.span`
    width: 40px;
    text-align: center;
    font-size: 16px;
    font-weight: 700;
`;

const More = styled(Button)`
    align-self: flex-start;
    min-height: 32px;
    margin: -6px 0 -6px -8px;
    padding: 0 8px;
    font-size: 14px;
`;

const Divider = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid ${color.border};
    font-size: 13px;
    line-height: 18px;
    color: ${color.textMuted};
`;
